import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPlayerComparison, } from "../api/players/createPlayerComparison";
import { evolutionPathPair } from "../evolutions/evolutionPath";
import { DEFAULT_SLOT_CHEM, applyBestChemstylesForRole, compareContextSelector, comparePlayerKey, comparePlayerSlotsSelector, comparePlayersSelector, fetchSuggestedComparePlayers, removeComparePlayer, setCompareChemStyleId, setCompareChemistry, setComparePlayers, } from "../redux/comparePlayers/comparePlayerSlice";
import { compareRoleLabelSelector, defaultPositions, playerRolesSelector, } from "../redux/players/playersSlice";
import { buildRoleGroups } from "../utils/playerRoles";
import { computeCategoryWinners, computeMetaRatingWinners, computeRoleMismatch, scoresComparable, } from "./compare";
/**
 * Shared Compare data flow, used by the web page/sheet and the mobile screen:
 * the silent comparison fetch (one per distinct id set + graded role + slot
 * chem), per-section tiers, winners, the graded-role state and the
 * role-picker groups.
 *
 * Platform concerns are injected instead of owned:
 * - `seedIdsParam` — the RAW route/query value the deep link carries (web:
 *   `searchParams.get("resourceIds")`, mobile: the `resourceIds` route param).
 *   Each app only has to read its own router; the parse, the once-on-mount
 *   guard and dropping the seed after its fetch lands are owned here, so the
 *   two wrappers can't drift apart on it. Repeated params arrive as `string[]`
 *   from expo-router, hence the union.
 * - `onIdsChange` — the current resource ids after a successful fetch or a
 *   remove; web mirrors them to the URL, mobile ignores it.
 */
export const useCompareData = ({ seedIdsParam, onIdsChange, } = {}) => {
    // Both apps mount a `playersReducer` carrying `playerRoles`, so the hook
    // reads the roles itself instead of having each host pass its own list.
    const playerRoles = useSelector(playerRolesSelector);
    const dispatch = useDispatch();
    // Slots pair each player with their saved chemistry/chemstyleId, in the same
    // column order as `players`.
    const slots = useSelector(comparePlayerSlotsSelector);
    const players = useSelector(comparePlayersSelector);
    // The slice is the source of truth; the route param only seeds the FIRST
    // fetch, and only when nothing is staged yet (a cold deep link). Resolved in
    // a lazy initializer rather than a mount effect so the very first render
    // already carries the seed — an effect would run one fetch cycle with no
    // seed first. `players` is read once here on purpose, so the seed can't be
    // re-evaluated after the user starts editing the set.
    const [seedIds, setSeedIds] = useState(() => {
        if (players.length > 0)
            return null;
        const raw = Array.isArray(seedIdsParam) ? seedIdsParam[0] : seedIdsParam;
        if (typeof raw !== "string")
            return null;
        const ids = raw.split(",").map(Number).filter(Number.isFinite);
        return ids.length > 0 ? ids : null;
    });
    // Per-player category breakdown from the comparison endpoint, keyed by
    // comparePlayerKey — a base card and its evolved versions share a
    // resourceId but are distinct columns (section headers read their tier
    // from it).
    const [comparisons, setComparisons] = useState({});
    // Optional role every player is graded under (undefined = each player's own
    // best role). Selecting a role silently refetches the comparison.
    const [playerRole, setPlayerRole] = useState(undefined);
    // Trigger-site context: staging from a squad-builder slot carries that
    // slot's role — the user is comparing for a specific spot, so grade under
    // it instead of "Best". Re-asserts on every new context dispatch (staging
    // again from a slot wins over a manual pick); manual picks in between stay
    // untouched since the context value is unchanged.
    const compareContext = useSelector(compareContextSelector);
    useEffect(() => {
        if (compareContext?.playerRoleId == null)
            return;
        const role = playerRoles.find((r) => r.id === compareContext.playerRoleId);
        if (role)
            setPlayerRole(role);
    }, [compareContext, playerRoles]);
    // Position groups for the role picker, in defaultPositions order (same as
    // the Player Details roles section). No ratings/tiers here: a single role
    // grades all compared players, so per-player meta doesn't apply. Roles no
    // compared player can play (position outside the union of their positions)
    // are disabled — "Show fewer" in the picker hides them.
    const roleGroups = buildRoleGroups({
        playerRoles,
        positionsOrder: defaultPositions.map(({ label }) => label),
        playablePositions: players.flatMap((p) => p.positions),
    });
    // One comparison fetch per distinct set of compared players (seed ids on
    // first load, slice ids after — including every add/remove) and selected
    // role. Deliberately silent, no loading state: the current cards stay on
    // screen and the response swaps in the detailed cards + fresh comparisons
    // at once.
    // Column identities, not bare resourceIds: an evo-identity change (say,
    // swapping which evolved version of a player is compared) must refetch even
    // though the id list is unchanged.
    const requestPlayerKeys = seedIds
        ? seedIds.map((resourceId) => comparePlayerKey({ resourceId }))
        : players.map(comparePlayerKey);
    // Per-slot chemistry/chemstyle are fetch inputs too — picking either
    // silently refetches the comparison the same way add/remove/role do. Seed
    // ids take the slot defaults (what their slots will hold after the fetch),
    // so seeding doesn't shift the key and refetch a second time.
    //
    // At 0 chem no style applies, so the fetch always asks for Basic (250)
    // there — the slot keeps the picked style, it just doesn't reach the
    // request until chemistry comes back up (0-chem + styled combos don't
    // exist in the ratings and would come back undefined).
    const effectiveChemstyleId = (slot) => slot.chemistry
        ? (slot.chemstyleId ?? DEFAULT_SLOT_CHEM.chemstyleId)
        : DEFAULT_SLOT_CHEM.chemstyleId;
    const slotChem = seedIds ? seedIds.map(() => DEFAULT_SLOT_CHEM) : slots;
    const slotChemKey = slotChem
        .map((slot) => `${slot.chemistry}:${effectiveChemstyleId(slot)}`)
        .join(",");
    const requestKey = `${requestPlayerKeys.join(",")}|${playerRole?.id ?? "best"}|${slotChemKey}`;
    const lastFetchedKey = useRef(null);
    useEffect(() => {
        if (lastFetchedKey.current === requestKey)
            return;
        lastFetchedKey.current = requestKey;
        if (requestPlayerKeys.length === 0) {
            setComparisons({});
            // Removing the last player still refreshes the suggestions — the empty
            // set fetches unfiltered (no price cap, no role): the overall best
            // players, instead of leaving the previous set's suggestions around.
            dispatch(fetchSuggestedComparePlayers({}));
            return;
        }
        let cancelled = false;
        const inputs = seedIds
            ? seedIds.map((resourceId) => ({ resourceId, ...DEFAULT_SLOT_CHEM }))
            : slots.map((slot) => {
                const evolutionPath = slot.player.evolutionData?.evolutionPath ||
                    slot.player.evolutionPath;
                return {
                    resourceId: slot.player.resourceId,
                    evoPathId: slot.player.evoPathId,
                    ...(evolutionPath &&
                        evolutionPathPair(evolutionPath, slot.player.evolutionData?.pathwayPath ||
                            slot.player.pathwayPath)),
                    chemistry: slot.chemistry,
                    chemstyleId: effectiveChemstyleId(slot),
                };
            });
        // The response cards don't reliably echo the evo identity the request
        // named, so re-attach it (per input, same order) — key matching in the
        // slice and the comparisons map depends on it.
        const withIdentity = (player, input) => ({
            ...player,
            ...(input?.evoPathId != null && { evoPathId: input.evoPathId }),
            ...(input?.evolutionPath != null && {
                evolutionPath: input.evolutionPath,
            }),
            ...(input?.pathwayPath != null && { pathwayPath: input.pathwayPath }),
        });
        createPlayerComparison(inputs, playerRole?.id)
            .then((entries) => {
            if (cancelled)
                return;
            const detailedPlayers = entries.map((entry, i) => withIdentity(entry.player, inputs[i]));
            dispatch(setComparePlayers(detailedPlayers));
            // Keep the ids shareable after adds too (removes report directly).
            onIdsChange?.(detailedPlayers.map((player) => player.resourceId));
            setComparisons(Object.fromEntries(entries.map((entry, i) => [
                comparePlayerKey(detailedPlayers[i] ?? entry.player),
                entry.comparisons,
            ])));
            // The seeded fetch has landed and its players are in the slice now —
            // drop the seed so every later fetch reads the slice.
            setSeedIds(null);
            // Prefetch the add-search suggestions for this compared set + role so
            // they're already there when the search view opens (its own mount
            // dispatch then no-ops via the thunk's condition). After
            // setComparePlayers, so the thunk reads the fresh set.
            dispatch(fetchSuggestedComparePlayers({ playerRoleId: playerRole?.id }));
        })
            .catch(() => {
            // Keep whatever is on screen; the next add/remove retries.
            if (!cancelled)
                lastFetchedKey.current = null;
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey, dispatch]);
    const comparable = scoresComparable(players, playerRole !== undefined);
    const metaRatingWinners = computeMetaRatingWinners(players, comparable);
    const mixedRoles = players.length > 1 && !comparable;
    // "Poacher ST" / "Best Role" — the label both role triggers render.
    const playerRoleLabel = useSelector(compareRoleLabelSelector(playerRole?.id));
    const roleMismatch = mixedRoles
        ? computeRoleMismatch(players, playerRoles)
        : undefined;
    // Rows are matched on the solver `key`, never on the English `label`.
    const category = (player, key) => comparisons[comparePlayerKey(player)]?.find((c) => c.key === key);
    /**
     * Winning column indexes for an endpoint category (`playStyles`,
     * `accelerate`, …), ranked by `score` — see `computeCategoryWinners`.
     * `disabled` — never the score's value — is what marks a category the
     * solver ignored for the graded role.
     */
    const categoryWinners = (key) => computeCategoryWinners(players.map((p) => {
        const c = category(p, key);
        return c && !c.disabled ? c.score : undefined;
    }), comparable);
    // Section-header tiers come from the endpoint's category breakdown:
    // `playStyles` grades the PlayStyles section, `igAttributes` the
    // Attributes section.
    const sectionTiers = (key) => {
        const winners = categoryWinners(key);
        return players.map((p, idx) => ({
            tier: category(p, key)?.tier,
            winner: winners.has(idx),
        }));
    };
    // Manual role picks re-derive every on-chem slot's chemstyle — the best
    // style is role-dependent, so the one picked under the old role rarely fits
    // the new one. (The squad-builder context assert doesn't go through here:
    // slots staged there carry the builder's own style for that role.)
    const selectPlayerRole = (role) => {
        setPlayerRole(role);
        dispatch(applyBestChemstylesForRole({ playerRoleId: role?.id }));
    };
    const setSlotChemistry = (player, chemistry) => dispatch(setCompareChemistry({ playerKey: comparePlayerKey(player), chemistry }));
    const setSlotChemStyleId = (player, chemstyleId) => dispatch(setCompareChemStyleId({
        playerKey: comparePlayerKey(player),
        chemstyleId,
    }));
    const removePlayer = (player) => {
        dispatch(removeComparePlayer(player));
        const removedKey = comparePlayerKey(player);
        onIdsChange?.(players
            .filter((p) => comparePlayerKey(p) !== removedKey)
            .map((p) => p.resourceId));
    };
    return {
        players,
        slots,
        sectionTiers,
        categoryWinners,
        metaRatingWinners,
        playerRole,
        setPlayerRole: selectPlayerRole,
        playerRoles,
        playerRoleLabel,
        roleGroups,
        roleMismatch,
        removePlayer,
        setSlotChemistry,
        setSlotChemStyleId,
    };
};
//# sourceMappingURL=useCompareData.js.map